package com.mgacreative.hotel.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.FilterList
import androidx.compose.material.icons.filled.Layers
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.domain.model.Room
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun RoomManagementScreen(
    onNavigateBack: () -> Unit = {},
    onNavigate: (String) -> Unit = {}
) {
    val viewModel = koinViewModel<AdminRoomViewModel>()
    val rooms by viewModel.rooms.collectAsState()
    val roomTypes = viewModel.roomTypes

    var newRoomNumber by remember { mutableStateOf("") }
    var selectedRoomType by remember { mutableStateOf<String?>(null) }
    var expandedRoomTypeDropdown by remember { mutableStateOf(false) }

    var selectedFilter by remember { mutableStateOf("Tümü") }
    var selectedStatusFilter by remember { mutableStateOf("Tümü") }
    var selectedFloorFilter by remember { mutableStateOf("Tümü") }
    var expandedFilterDropdown by remember { mutableStateOf(false) }
    var expandedFloorDropdown by remember { mutableStateOf(false) }

    // Oda numarasına göre sayısal sıralama yardımcısı
    fun parseRoomNumber(numStr: String): Int {
        val digits = numStr.filter { it.isDigit() }
        return digits.toIntOrNull() ?: Int.MAX_VALUE
    }

    // Kat hesaplama: 101 -> 1. Kat, 1002 -> 10. Kat, 25 -> 2. Kat
    fun getFloorName(roomNumber: String): String {
        val digits = roomNumber.filter { it.isDigit() }
        return when {
            digits.length >= 3 -> "${digits.dropLast(2)}. Kat"
            digits.isNotEmpty() -> "${digits.first()}. Kat"
            else -> "Genel / Zemin"
        }
    }

    // Filtrelenmiş ve sayısal olarak kusursuz sıralanmış odalar
    val sortedAndFilteredRooms = rooms
        .filter { room ->
            val typeMatch = (selectedFilter == "Tümü") || (room.roomType == selectedFilter)
            val statusMatch = (selectedStatusFilter == "Tümü") || (room.status.equals(selectedStatusFilter, ignoreCase = true))
            val floorMatch = (selectedFloorFilter == "Tümü") || (getFloorName(room.roomNumber) == selectedFloorFilter)
            typeMatch && statusMatch && floorMatch
        }
        .sortedWith(compareBy({ parseRoomNumber(it.roomNumber) }, { it.roomNumber }))

    // Katlara göre gruplama
    val roomsByFloor = sortedAndFilteredRooms.groupBy { getFloorName(it.roomNumber) }
    val allFloorNames = rooms.map { getFloorName(it.roomNumber) }.distinct().sortedWith(compareBy { 
        it.filter { c -> c.isDigit() }.toIntOrNull() ?: 999 
    })

    // Dialog state
    var selectedRoomForEdit by remember { mutableStateOf<Room?>(null) }
    var editRoomNumber by remember { mutableStateOf("") }
    var editRoomType by remember { mutableStateOf("") }
    var editRoomStatus by remember { mutableStateOf("") }
    var expandedEditDropdown by remember { mutableStateOf(false) }
    var expandedStatusDropdown by remember { mutableStateOf(false) }
    val availableStatuses = listOf("CLEAN", "AVAILABLE", "OCCUPIED", "MAINTENANCE", "DIRTY")

    LaunchedEffect(Unit) {
        viewModel.loadData()
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text(
                            text = "Oda Yönetimi & Kat Planı",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0F172A)
                        )
                        Text(
                            text = "Toplam ${rooms.size} Oda (${sortedAndFilteredRooms.size} listeleniyor - ${roomsByFloor.size} Kat)",
                            style = MaterialTheme.typography.bodySmall,
                            color = Color(0xFF64748B)
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Geri", tint = Color(0xFF0F172A))
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color.White
                )
            )
        },
        containerColor = Color(0xFFF8FAFC)
    ) { innerPadding ->
        val listState = androidx.compose.foundation.lazy.rememberLazyListState()
        LazyColumn(
            state = listState,
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .padding(horizontal = 16.dp)
                .verticalScrollbar(listState),
            contentPadding = PaddingValues(bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            item {
                Spacer(modifier = Modifier.height(4.dp))

                // 1. Yeni Oda Ekleme Paneli (Saf Beyaz & Kurumsal)
                Card(
                    shape = RoundedCornerShape(12.dp),
                    colors = CardDefaults.cardColors(containerColor = Color.White),
                    border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(14.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                imageVector = Icons.Default.Add,
                                contentDescription = null,
                                tint = Color(0xFF2563EB),
                                modifier = Modifier.size(20.dp)
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = "Yeni Oda Tanımla",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.SemiBold,
                                color = Color(0xFF0F172A)
                            )
                        }

                        Spacer(modifier = Modifier.height(12.dp))

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(10.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            // Oda Tipi Seçim
                            Box(modifier = Modifier.weight(1f)) {
                                OutlinedTextField(
                                    value = selectedRoomType ?: "",
                                    onValueChange = {},
                                    placeholder = { Text("Oda Tipi Seçin", fontSize = 14.sp) },
                                    readOnly = true,
                                    shape = RoundedCornerShape(8.dp),
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable { expandedRoomTypeDropdown = !expandedRoomTypeDropdown },
                                    trailingIcon = {
                                        IconButton(onClick = { expandedRoomTypeDropdown = !expandedRoomTypeDropdown }) {
                                            Icon(Icons.Default.ArrowDropDown, contentDescription = "Seç", tint = Color(0xFF64748B))
                                        }
                                    },
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedContainerColor = Color.White,
                                        unfocusedContainerColor = Color.White,
                                        focusedBorderColor = Color(0xFF2563EB),
                                        unfocusedBorderColor = Color(0xFFCBD5E1)
                                    )
                                )
                                DropdownMenu(
                                    expanded = expandedRoomTypeDropdown,
                                    onDismissRequest = { expandedRoomTypeDropdown = false },
                                    modifier = Modifier.background(Color.White)
                                ) {
                                    roomTypes.forEach { type ->
                                        DropdownMenuItem(
                                            text = { Text(type, color = Color(0xFF1E293B)) },
                                            onClick = {
                                                selectedRoomType = type
                                                expandedRoomTypeDropdown = false
                                            }
                                        )
                                    }
                                }
                            }

                            // Oda Numarası Girişi
                            OutlinedTextField(
                                value = newRoomNumber,
                                onValueChange = { newRoomNumber = it },
                                placeholder = { Text("Oda No (Örn: 101)", fontSize = 14.sp) },
                                singleLine = true,
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.weight(1f),
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedContainerColor = Color.White,
                                    unfocusedContainerColor = Color.White,
                                    focusedBorderColor = Color(0xFF2563EB),
                                    unfocusedBorderColor = Color(0xFFCBD5E1)
                                )
                            )

                            // Ekle Butonu
                            Button(
                                onClick = {
                                    if (newRoomNumber.isNotBlank() && selectedRoomType != null) {
                                        viewModel.addRoom(newRoomNumber.trim(), selectedRoomType!!)
                                        newRoomNumber = ""
                                    }
                                },
                                shape = RoundedCornerShape(8.dp),
                                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                                modifier = Modifier.height(54.dp),
                                contentPadding = PaddingValues(horizontal = 20.dp)
                            ) {
                                Text("Oda Ekle", fontWeight = FontWeight.SemiBold, color = Color.White)
                            }
                        }
                    }
                }
            }

            item {
                // 2. Filtreleme Çubuğu (Kat + Tip + Durum Filtreleri)
                Card(
                    shape = RoundedCornerShape(10.dp),
                    colors = CardDefaults.cardColors(containerColor = Color.White),
                    border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            imageVector = Icons.Default.FilterList,
                            contentDescription = null,
                            tint = Color(0xFF64748B),
                            modifier = Modifier.size(18.dp)
                        )
                        Text("Filtrele:", fontSize = 13.sp, fontWeight = FontWeight.Medium, color = Color(0xFF64748B))

                        // Kat Filtresi Dropdown
                        Box(modifier = Modifier.weight(1f)) {
                            Surface(
                                shape = RoundedCornerShape(6.dp),
                                border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
                                color = Color(0xFFF8FAFC),
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable { expandedFloorDropdown = !expandedFloorDropdown }
                            ) {
                                Row(
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = "Kat: $selectedFloorFilter",
                                        fontSize = 13.sp,
                                        color = Color(0xFF1E293B),
                                        maxLines = 1,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                    Icon(Icons.Default.ArrowDropDown, contentDescription = null, tint = Color(0xFF64748B))
                                }
                            }
                            DropdownMenu(
                                expanded = expandedFloorDropdown,
                                onDismissRequest = { expandedFloorDropdown = false },
                                modifier = Modifier.background(Color.White)
                            ) {
                                DropdownMenuItem(
                                    text = { Text("Tüm Katlar", color = Color(0xFF1E293B)) },
                                    onClick = {
                                        selectedFloorFilter = "Tümü"
                                        expandedFloorDropdown = false
                                    }
                                )
                                allFloorNames.forEach { floor ->
                                    DropdownMenuItem(
                                        text = { Text(floor, color = Color(0xFF1E293B)) },
                                        onClick = {
                                            selectedFloorFilter = floor
                                            expandedFloorDropdown = false
                                        }
                                    )
                                }
                            }
                        }

                        // Tip Filtresi Dropdown
                        Box(modifier = Modifier.weight(1f)) {
                            Surface(
                                shape = RoundedCornerShape(6.dp),
                                border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
                                color = Color(0xFFF8FAFC),
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable { expandedFilterDropdown = !expandedFilterDropdown }
                            ) {
                                Row(
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = "Tip: $selectedFilter",
                                        fontSize = 13.sp,
                                        color = Color(0xFF1E293B),
                                        maxLines = 1,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                    Icon(Icons.Default.ArrowDropDown, contentDescription = null, tint = Color(0xFF64748B))
                                }
                            }
                            DropdownMenu(
                                expanded = expandedFilterDropdown,
                                onDismissRequest = { expandedFilterDropdown = false },
                                modifier = Modifier.background(Color.White)
                            ) {
                                DropdownMenuItem(
                                    text = { Text("Tüm Tipler", color = Color(0xFF1E293B)) },
                                    onClick = {
                                        selectedFilter = "Tümü"
                                        expandedFilterDropdown = false
                                    }
                                )
                                roomTypes.forEach { type ->
                                    DropdownMenuItem(
                                        text = { Text(type, color = Color(0xFF1E293B)) },
                                        onClick = {
                                            selectedFilter = type
                                            expandedFilterDropdown = false
                                        }
                                    )
                                }
                            }
                        }

                        // Durum Filtre Çipleri
                        val statusOptions = listOf("Tümü", "CLEAN", "OCCUPIED", "MAINTENANCE")
                        statusOptions.forEach { status ->
                            val isSelected = selectedStatusFilter == status
                            Surface(
                                shape = RoundedCornerShape(16.dp),
                                color = if (isSelected) Color(0xFF2563EB) else Color(0xFFF1F5F9),
                                modifier = Modifier.clickable { selectedStatusFilter = status }
                            ) {
                                Text(
                                    text = if (status == "Tümü") "Tümü" else status,
                                    fontSize = 12.sp,
                                    fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
                                    color = if (isSelected) Color.White else Color(0xFF475569),
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp)
                                )
                            }
                        }
                    }
                }
            }

            // 3. Kat Kat Sıralanmış ve Sayısal Olarak Dizilmiş Oda Bölümleri
            if (roomsByFloor.isEmpty()) {
                item {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(48.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "Filtrelere uygun oda bulunamadı.",
                            color = Color(0xFF94A3B8),
                            style = MaterialTheme.typography.bodyLarge
                        )
                    }
                }
            } else {
                items(roomsByFloor.keys.toList(), key = { it }) { floorName ->
                    val floorRooms = roomsByFloor[floorName] ?: emptyList()

                    Card(
                        shape = RoundedCornerShape(12.dp),
                        colors = CardDefaults.cardColors(containerColor = Color.White),
                        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            // Kat Başlığı ve Oda Sayısı
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(
                                        imageVector = Icons.Default.Layers,
                                        contentDescription = null,
                                        tint = Color(0xFF2563EB),
                                        modifier = Modifier.size(18.dp)
                                    )
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text(
                                        text = floorName,
                                        fontSize = 16.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = Color(0xFF0F172A)
                                    )
                                }
                                Surface(
                                    shape = RoundedCornerShape(12.dp),
                                    color = Color(0xFFF1F5F9)
                                ) {
                                    Text(
                                        text = "${floorRooms.size} Oda",
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.Medium,
                                        color = Color(0xFF475569),
                                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
                                    )
                                }
                            }

                            Spacer(modifier = Modifier.height(12.dp))

                            // Bu Kata Ait Odaların Responsive Izgara (Rack) Dizilimi
                            FlowRow(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(10.dp),
                                verticalArrangement = Arrangement.spacedBy(10.dp),
                                maxItemsInEachRow = Int.MAX_VALUE
                            ) {
                                floorRooms.forEach { room ->
                                    RoomRackCard(
                                        room = room,
                                        modifier = Modifier.width(135.dp),
                                        onClick = {
                                            selectedRoomForEdit = room
                                            editRoomNumber = room.roomNumber
                                            editRoomType = room.roomType
                                            editRoomStatus = room.status
                                        }
                                    )
                                }
                            }

                            Spacer(modifier = Modifier.height(12.dp))

                            // Kat Krokisini Düzenle Butonu (Sağ Alt Köşe)
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.End
                            ) {
                                OutlinedButton(
                                    onClick = { onNavigate("FloorPlanEditorScreen") },
                                    shape = RoundedCornerShape(8.dp),
                                    border = BorderStroke(1.dp, Color(0xFF2563EB)),
                                    colors = ButtonDefaults.outlinedButtonColors(
                                        containerColor = Color(0xFFEFF6FF),
                                        contentColor = Color(0xFF2563EB)
                                    ),
                                    contentPadding = PaddingValues(horizontal = 14.dp, vertical = 6.dp)
                                ) {
                                    Icon(
                                        imageVector = Icons.Default.Layers,
                                        contentDescription = null,
                                        tint = Color(0xFF2563EB),
                                        modifier = Modifier.size(16.dp)
                                    )
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text(
                                        text = "Kat Krokisini Düzenle",
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = Color(0xFF2563EB)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // 4. Düzenle / Sil Dialog (Saf Beyaz & Kurumsal Modal)
    if (selectedRoomForEdit != null) {
        AlertDialog(
            onDismissRequest = { selectedRoomForEdit = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(16.dp),
            title = {
                Text(
                    text = "Oda Düzenle (#${selectedRoomForEdit?.roomNumber})",
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF0F172A),
                    fontSize = 18.sp
                )
            },
            text = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // Tip Seçimi
                    Box {
                        OutlinedTextField(
                            value = editRoomType,
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Oda Tipi") },
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { expandedEditDropdown = !expandedEditDropdown },
                            trailingIcon = {
                                IconButton(onClick = { expandedEditDropdown = !expandedEditDropdown }) {
                                    Icon(Icons.Default.ArrowDropDown, contentDescription = "Seç", tint = Color(0xFF64748B))
                                }
                            },
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color.White,
                                focusedBorderColor = Color(0xFF2563EB),
                                unfocusedBorderColor = Color(0xFFCBD5E1)
                            )
                        )
                        DropdownMenu(
                            expanded = expandedEditDropdown,
                            onDismissRequest = { expandedEditDropdown = false },
                            modifier = Modifier.background(Color.White)
                        ) {
                            roomTypes.forEach { type ->
                                DropdownMenuItem(
                                    text = { Text(type, color = Color(0xFF1E293B)) },
                                    onClick = {
                                        editRoomType = type
                                        expandedEditDropdown = false
                                    }
                                )
                            }
                        }
                    }

                    // Durum Seçimi
                    Box {
                        OutlinedTextField(
                            value = editRoomStatus,
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Oda Durumu") },
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { expandedStatusDropdown = !expandedStatusDropdown },
                            trailingIcon = {
                                IconButton(onClick = { expandedStatusDropdown = !expandedStatusDropdown }) {
                                    Icon(Icons.Default.ArrowDropDown, contentDescription = "Seç", tint = Color(0xFF64748B))
                                }
                            },
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color.White,
                                focusedBorderColor = Color(0xFF2563EB),
                                unfocusedBorderColor = Color(0xFFCBD5E1)
                            )
                        )
                        DropdownMenu(
                            expanded = expandedStatusDropdown,
                            onDismissRequest = { expandedStatusDropdown = false },
                            modifier = Modifier.background(Color.White)
                        ) {
                            availableStatuses.forEach { status ->
                                DropdownMenuItem(
                                    text = { Text(status, color = Color(0xFF1E293B)) },
                                    onClick = {
                                        editRoomStatus = status
                                        expandedStatusDropdown = false
                                    }
                                )
                            }
                        }
                    }

                    // Oda Numarası
                    OutlinedTextField(
                        value = editRoomNumber,
                        onValueChange = { editRoomNumber = it },
                        label = { Text("Oda Numarası") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color.White,
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1)
                        )
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        selectedRoomForEdit?.let { room ->
                            viewModel.updateRoom(room.id ?: "", editRoomType, editRoomNumber, editRoomStatus)
                        }
                        selectedRoomForEdit = null
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text("Kaydet", color = Color.White, fontWeight = FontWeight.SemiBold)
                }
            },
            dismissButton = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    TextButton(
                        onClick = {
                            selectedRoomForEdit?.let { room ->
                                viewModel.deleteRoom(room.id ?: "")
                            }
                            selectedRoomForEdit = null
                        },
                        colors = ButtonDefaults.textButtonColors(contentColor = Color(0xFFDC2626))
                    ) {
                        Text("Odayı Sil", fontWeight = FontWeight.Medium)
                    }
                    Spacer(modifier = Modifier.width(4.dp))
                    TextButton(
                        onClick = { selectedRoomForEdit = null },
                        colors = ButtonDefaults.textButtonColors(contentColor = Color(0xFF64748B))
                    ) {
                        Text("Vazgeç")
                    }
                }
            }
        )
    }
}

/**
 * Kompakt Rack Oda Kartı - 200+ Oda İçin Hızlı Taranabilir ve Temiz Tasarım
 */
@Composable
private fun RoomRackCard(
    room: Room,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val statusUpper = room.status.uppercase()
    val (statusBgColor, statusTextColor, borderColor) = when {
        statusUpper.contains("CLEAN") || statusUpper.contains("AVAIL") -> Triple(Color(0xFFDCFCE7), Color(0xFF15803D), Color(0xFFBBF7D0))
        statusUpper.contains("OCCUPIED") -> Triple(Color(0xFFDBEAFE), Color(0xFF1D4ED8), Color(0xFFBFDBFE))
        statusUpper.contains("MAINT") -> Triple(Color(0xFFFEF3C7), Color(0xFFB45309), Color(0xFFFDE68A))
        statusUpper.contains("DIRTY") -> Triple(Color(0xFFFFE4E6), Color(0xFFBE123C), Color(0xFFFECDD3))
        else -> Triple(Color(0xFFF1F5F9), Color(0xFF475569), Color(0xFFE2E8F0))
    }

    Card(
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = modifier.clickable { onClick() }
    ) {
        Column(
            modifier = Modifier.padding(10.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            // Üst Satır: Oda No + Durum Rozeti
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = room.roomNumber,
                    fontSize = 17.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = Color(0xFF0F172A)
                )

                Surface(
                    shape = RoundedCornerShape(4.dp),
                    color = statusBgColor,
                    border = BorderStroke(1.dp, borderColor)
                ) {
                    Text(
                        text = room.status.take(6),
                        fontSize = 10.sp,
                        fontWeight = FontWeight.Bold,
                        color = statusTextColor,
                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                    )
                }
            }

            // Alt Satır: Oda Tipi
            Text(
                text = room.roomType,
                fontSize = 12.sp,
                color = Color(0xFF64748B),
                fontWeight = FontWeight.Medium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}
